import * as React from "react";

/**
 * Pills and badges, generalised from ScadsysIdentity's CountPill / StatusBadge in
 * dataMaintenance/DataMaintenanceIndex.tsx — the most refined presentational vocabulary in that repo.
 */

export type BadgeTone = "neutral" | "primary" | "success" | "warning" | "danger" | "info";

const TONES: Record<BadgeTone, string> = {
    neutral: "bg-surface-muted text-text-weak ring-border",
    primary: "bg-primary-soft text-primary-text ring-primary",
    success: "bg-success-soft text-success-text ring-success-border",
    warning: "bg-warning-soft text-warning-text ring-warning-border",
    danger: "bg-danger-soft text-danger-text ring-danger-border",
    info: "bg-info-soft text-info-text ring-info-border",
};

export interface BadgeProps {
    tone?: BadgeTone;
    children: React.ReactNode;
    className?: string;
    title?: string;
}

export const Badge: React.FC<BadgeProps> = ({ tone = "neutral", children, className = "", title }) => (
    <span
        title={title}
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONES[tone]} ${className}`}
    >
        {children}
    </span>
);

/** A count next to a heading. */
export const CountPill: React.FC<{ count: number; tone?: BadgeTone }> = ({ count, tone = "neutral" }) => (
    <Badge tone={tone}>{count}</Badge>
);

/**
 * Tone for the domain's workflow vocabulary — FinalizationStatus, ModerationStatus, ResultOrigin,
 * EnrollmentStatus, procedure Draft/Published, and the preview actions. Kept in one place so the
 * same word never gets two colours on two screens.
 */
const STATUS_TONES: Record<string, BadgeTone> = {
    // finalization / moderation
    Open: "neutral",
    Finalized: "success",
    None: "neutral",
    Pending: "warning",
    Approved: "success",
    Rejected: "danger",
    // origin
    Manual: "info",
    Calculated: "primary",
    Imported: "neutral",
    // procedure versions
    Draft: "warning",
    Published: "success",
    // enrollment
    Applied: "info",
    Enrolled: "success",
    Completed: "primary",
    Inactive: "neutral",
    Archive: "neutral",
    // calculation preview actions
    Create: "success",
    Update: "info",
    Unchanged: "neutral",
    Delete: "danger",
    Blocked: "danger",
    // finding severity
    Information: "info",
    Warning: "warning",
    Error: "danger",
};

export const StatusBadge: React.FC<{ status?: string | null; title?: string }> = ({ status, title }) =>
    status ? (
        <Badge tone={STATUS_TONES[status] ?? "neutral"} title={title}>
            {status}
        </Badge>
    ) : null;
